import { useTranslation } from 'next-i18next';
import { splitAt } from 'ramda';
import { useMemo } from 'react';
import { ISupplier } from '@bridebook/models/source/models/Suppliers.types';
import { slugify } from '@bridebook/toolbox';
import { Box, CopyToClipboard } from '@bridebook/ui';
import SectionHeading from '@bridebook/web/app-shared/components/supplier/sections/section-heading';
import SectionText from '@bridebook/web/app-shared/components/supplier/sections/section-text/section-text';
import { formatAddress } from '@bridebook/web/app-shared/lib/supplier/utils';
import componentStyles from '@bridebook/web/app-basic/components/supplier/sections/location-contact-details/directions.style';

interface IProps {
  supplier: ISupplier;
  includeAddress?: boolean;
}

const Directions = ({ supplier, includeAddress = true }: IProps) => {
  const nearestTown = supplier?.directions?.nearest?.postalTown;
  const nearestTrainStation = supplier?.directions?.nearest?.trainStation;
  const nearestMotorway = supplier?.directions?.nearest?.highway;
  const nearestAirport = supplier?.directions?.nearest?.airport;
  const travelNotes = supplier?.directions?.details;
  const supplierType = supplier?.type;

  const { t } = useTranslation('supplier');
  const supplierAddress = formatAddress(supplier);

  const fields = useMemo(
    () => [
      ...(includeAddress
        ? [
            {
              title: supplierType.includes('venue')
                ? t('contact.address.venue')
                : t('contact.address'),
              text: supplierAddress,
              hasCopyToClipboard: true,
            },
          ]
        : []),
      {
        title: t('contact.notes'),
        text: travelNotes,
        hasCopyToClipboard: false,
      },
      {
        title: t('contact.town'),
        text: nearestTown,
        hasCopyToClipboard: false,
      },
      {
        title: t('contact.motorway'),
        text: nearestMotorway,
        hasCopyToClipboard: false,
      },
      {
        title: t('contact.train'),
        text: nearestTrainStation,
        hasCopyToClipboard: false,
      },
      {
        title: t('contact.airport'),
        text: nearestAirport,
        hasCopyToClipboard: false,
      },
    ],
    [
      t,
      supplierAddress,
      travelNotes,
      nearestTown,
      nearestMotorway,
      nearestTrainStation,
      nearestAirport,
      supplierType,
      includeAddress,
    ],
  );

  // remove all empty data
  const fieldSet = fields.filter((item) => item.text);

  // split data to columns
  const navItems =
    fieldSet.length > 1 ? splitAt(Math.round(fieldSet.length / 2), fieldSet) : [fieldSet];

  const styles = componentStyles();

  return fieldSet.length > 0 ? (
    <Box id="location" data-purpose="profile-directions" style={styles.wrapper}>
      {navItems.map((column) => (
        <Box style={styles.column} key={`supplier-directions-column-${slugify(column[0].title)}`}>
          {column.map((item) => (
            <Box key={`supplier-directions-item-${slugify(item.title)}`}>
              <SectionHeading lineHeight={20}>{item.title}</SectionHeading>
              <SectionText lineHeight={20}>
                {item.text}
                {item.hasCopyToClipboard && (
                  <CopyToClipboard
                    width={14}
                    style={styles.copyToClipboardButton}
                    text={item.text || ''}
                  />
                )}
              </SectionText>
            </Box>
          ))}
        </Box>
      ))}
    </Box>
  ) : null;
};

export default Directions;
